import React, { RefObject, useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { AudioDataResult } from "interfaces/sessions";
import { useDispatch } from 'react-redux';
import { startDownload, downloadSuccess, downloadError } from '../../../../lib/redux/sessions/audio/audio-slice';

type AudioElements = {
  start: number,
  duration: number,
  ref: RefObject<HTMLAudioElement>
  element: JSX.Element
}

async function fetchAndDecodeAudio(context: AudioContext, url: string): Promise<AudioBuffer | null> {
  try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      return await context.decodeAudioData(arrayBuffer);
  } catch (error) {
      console.error('Failed to fetch or decode audio:', error);
      return null;
  }
}

async function combineAndDownloadAudio(
  filename: string, 
  audioElements: AudioElements[], 
  dispatch: any, 
  sessionId: string
): Promise<void> {
  try {
    dispatch(startDownload(sessionId));
    
    const audioContext = new (window.AudioContext)();

    // Fetch and decode both audio files, or use silence if null
    type AudioSegment = {'buffer': AudioBuffer, 'offset': number};
    const audioSegments: AudioSegment[] = (await Promise.all(audioElements.map(async a => {
        const url = a.ref.current?.src;
        const buffer = url?.length ? await fetchAndDecodeAudio(audioContext, url) : null;
        return buffer === null ? null : {
          'buffer': buffer,
          'offset': Math.round(audioContext.sampleRate * a.start / 1000),
        };
    }))).filter((a): a is AudioSegment => a !== null);

    // If both are null, nothing to do
    if (!audioSegments.length) {
        console.error('All audio sources are null. No audio to combine.');
        dispatch(downloadError({ sessionId, error: 'No audio data available to download' }));
        return;
    }

    // Determine the max length of both buffers
    // The AudioContext API guarantees that .decodeAudioData() produced buffers in audioContext's native sample rate.
    const maxLength = Math.max(...audioSegments.map(a => a.buffer.length + a.offset));
    const numberOfChannels = Math.max(...audioSegments.map(a => a.buffer.numberOfChannels || 1));

    // Create a new AudioBuffer with the combined length and channels
    const combinedBuffer = audioContext.createBuffer(numberOfChannels, maxLength, audioContext.sampleRate);

    // Function to mix audio data into the combined buffer
    function mixBuffer(sourceBuffer: AudioBuffer | null, targetBuffer: AudioBuffer, channelOffset: number = 0, sampleOffset: number = 0): void {
        if (!sourceBuffer) return;
        for (let channel = 0; channel < sourceBuffer.numberOfChannels; channel++) {
            const sourceData = sourceBuffer.getChannelData(channel);
            const targetData = targetBuffer.getChannelData(channel + channelOffset);
            for (let i = 0; i < sourceData.length; i++) {
                targetData[i + sampleOffset] += sourceData[i];
            }
        }
    }

    // Mix audioSegments into combinedBuffer
    for (const audioSegment of audioSegments) {
      mixBuffer(audioSegment.buffer, combinedBuffer, 0, audioSegment.offset);
    }

    // Encode the combined buffer to a WAV file
    const wavBlob = await encodeWAV(combinedBuffer);

    // Create a downloadable link
    const url = URL.createObjectURL(wavBlob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `${filename}.wav`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    
    // Dispatch success action
    dispatch(downloadSuccess(sessionId));
    
  } catch (error) {
    console.error('Failed to download audio:', error);
    dispatch(downloadError({ 
      sessionId, 
      error: error instanceof Error ? error.message : 'Failed to download audio' 
    }));
  }
}

function writeUTFBytes(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
  }
}
async function encodeWAV(audioBuffer: AudioBuffer): Promise<Blob> {
  const numberOfChannels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length * numberOfChannels * 2 + 44;
  const buffer = new ArrayBuffer(length);
  const view = new DataView(buffer);

  writeUTFBytes(view, 0, 'RIFF');
  view.setUint32(4, length - 8, true);
  writeUTFBytes(view, 8, 'WAVE');
  writeUTFBytes(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numberOfChannels, true);
  view.setUint32(24, audioBuffer.sampleRate, true);
  view.setUint32(28, audioBuffer.sampleRate * numberOfChannels * 2, true);
  view.setUint16(32, numberOfChannels * 2, true);
  view.setUint16(34, 16, true);
  writeUTFBytes(view, 36, 'data');
  view.setUint32(40, length - 44, true);

  let offset = 44;
  for (let i = 0; i < audioBuffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
          const sample = Math.max(-1, Math.min(1, audioBuffer.getChannelData(channel)[i]));
          view.setInt16(offset, sample * 0x7FFF, true);
          offset += 2;
      }
  }

  return new Blob([view], { type: 'audio/wav' });
}

export type AudioPlayerProps = {
  sessionId: string,
  speed: number,
  isPlaying: boolean,
  currentTimeInMS: number,
  data: AudioDataResult
}

// Define the type for the ref
export type AudioPlayerRef = {
  downloadAudio: () => void;
};

export const AudioPlayer = forwardRef<AudioPlayerRef, AudioPlayerProps>(
  ({sessionId, speed, isPlaying, currentTimeInMS, data}, ref) => {
    const dispatch = useDispatch();
    const audioElementsRef = useRef<AudioElements[] | undefined>();

    const _transformAudioData = (data: AudioDataResult) => {
      const start = data.session.started_at
      const adjustedAudioMap = data.audio.map((x, idx) => {return {'value': x, 'idx': idx}}).filter((v) => v.value.audio_status === 'UPLOADED' )
      return adjustedAudioMap.map((val) => {
        const audioRef: RefObject<HTMLAudioElement> = React.createRef()
        const audioElement: JSX.Element = <audio ref={audioRef} src={data.audio_links![val.idx]} />
        return {
          'start': val.value.start.getTime() - start.getTime(),
          'duration': val.value.end.getTime() - val.value.start.getTime(),
          'ref': audioRef,
          'element': audioElement,
        }
      })
    }

    const [audioElements, setAudioElements] = useState<AudioElements[] | undefined>(_transformAudioData(data))

    useEffect(() => {
      const newElements = _transformAudioData(data);
      setAudioElements(newElements);
      audioElementsRef.current = newElements;
    }, [sessionId, data])

    useEffect(() => {
      if (!isPlaying) return
      audioElements?.forEach((value: AudioElements) => {
          if (value.ref.current && !value.ref.current.paused) {
            value.ref.current.playbackRate = speed
          }
        })
    }, [speed])

    useEffect(() => {
      if (!isPlaying) return
      audioElements?.forEach((value: AudioElements) => {
        if (value.ref.current && value.ref.current.paused && value.start < currentTimeInMS && (value.duration + value.start) > currentTimeInMS) {
          value.ref.current.currentTime = (currentTimeInMS - value.start)/1000
          value.ref.current.playbackRate = speed
          value.ref.current.play().catch(error => {
            console.error('Audio play failed:', error)
          })
        } 
      })
    }, [currentTimeInMS, isPlaying])
      
    useEffect(() => {
      if (isPlaying) return
      audioElements?.forEach((value: AudioElements) => {
        if (value.ref.current) {
          value.ref.current.pause()
        }
      })
    }, [isPlaying])

    const downloadAudio = () => {
      if (audioElements && audioElements.length > 0) {
        const filename = `Trellus Audio Call - ${data?.session.session_id ?? ''}`
        combineAndDownloadAudio(filename, audioElements, dispatch, sessionId)
      } else {
        dispatch(downloadError({ sessionId, error: 'No audio data available to download' }));
      }
    }

    // Expose downloadAudio function via ref
    useImperativeHandle(ref, () => ({
      downloadAudio
    }));
   
    // Cleanup audio elements on unmount to prevent memory leaks
    useEffect(() => {
      return () => {
        audioElementsRef.current?.forEach((value: AudioElements) => {
          if (value.ref.current) {
            value.ref.current.pause()
            value.ref.current.removeAttribute('src')
            value.ref.current.load()
          }
        })
      }
    }, [])

    return (
      <div>
        {audioElements?.map((value: AudioElements, index) => {
          return <div key={index}>{value.element}</div>})
        }
      </div>
    )
  }
);



  